// Small UI helpers shared by pages and components (no data or recorder logic here).
import { useEffect, useRef, useState } from 'react';
import type { Clip, DocType } from '../../../shared/types';
import { ERROR_LABEL, PENDING_LABEL } from '../export/common';
import { ApiClientError } from '../lib/http';
import type { Route } from '../lib/router';
import { toast } from './Toasts';

/**
 * UUID v4. crypto.randomUUID() only exists in secure contexts; on plain HTTP (e.g. a LAN
 * address during testing) fall back to getRandomValues, which is available everywhere.
 */
export function newId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6]! & 0x0f) | 0x40;
  b[8] = (b[8]! & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isUuid(value: unknown): value is string {
  return typeof value === 'string' && UUID_RE.test(value);
}

export function isDocType(value: unknown): value is DocType {
  return value === 'meeting' || value === 'inspection' || value === 'note';
}

export function routeForDocument(doc: { id: string; type: DocType }): Route {
  switch (doc.type) {
    case 'meeting':
      return { name: 'meeting', id: doc.id };
    case 'inspection':
      return { name: 'inspection', id: doc.id };
    case 'note':
      return { name: 'notes', id: doc.id };
  }
}

/** User-facing (Slovenian) text for an error thrown by the data layer or the recorder. */
export function errorText(err: unknown, fallback: string): string {
  if (err instanceof ApiClientError) return err.message || fallback;
  if (err instanceof Error && err.message && err.message !== 'not implemented') return err.message;
  return fallback;
}

/** navigator.share() rejects with AbortError when the user closes the share sheet. */
export function isAbortError(err: unknown): boolean {
  return typeof err === 'object' && err !== null && (err as { name?: unknown }).name === 'AbortError';
}

const numberFmt = new Intl.NumberFormat('sl-SI', { maximumFractionDigits: 1 });

/** 1_234_567 -> "1,2 MB" */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 MB';
  if (bytes < 1024 * 1024) return `${numberFmt.format(bytes / 1024)} kB`;
  if (bytes < 1024 * 1024 * 1024) return `${numberFmt.format(bytes / (1024 * 1024))} MB`;
  return `${numberFmt.format(bytes / (1024 * 1024 * 1024))} GB`;
}

/** True when keyboard input belongs to a text field (shortcuts must not fire there). */
export function isTextInputTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  if (target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement) return true;
  if (target instanceof HTMLInputElement) {
    return !['button', 'checkbox', 'radio', 'range', 'reset', 'submit', 'file', 'color', 'image'].includes(target.type);
  }
  return false;
}

export function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;
}

/** `value`, but only after it has not changed for `ms` milliseconds. */
export function useDebouncedValue<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

/** Current time, refreshed every `intervalMs` (for "overdue" highlighting and due checks). */
export function useNow(intervalMs: number): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), intervalMs);
    return () => clearInterval(timer);
  }, [intervalMs]);
  return now;
}

/** Ref that always holds the latest value (for stable callbacks that read fresh props). */
export function useLatest<T>(value: T): { readonly current: T } {
  const ref = useRef(value);
  useEffect(() => {
    ref.current = value;
  });
  return ref;
}

// ---------- saving drafts before the page goes away ----------

const hideFlushers = new Set<() => void>();
let hideListenersInstalled = false;

function flushOnHide(): void {
  for (const flush of [...hideFlushers]) {
    try {
      flush();
    } catch (err) {
      console.error('[drafts] saving before the page is hidden failed', err);
    }
  }
}

/**
 * Runs `flush` when the page is hidden or unloaded (app switch, reload, pull-to-refresh, closed or
 * discarded tab) until the returned function is called. Editors use it so text that is still being
 * typed is saved even though no blur event comes.
 */
export function onPageHide(flush: () => void): () => void {
  if (!hideListenersInstalled && typeof window !== 'undefined') {
    hideListenersInstalled = true;
    window.addEventListener('pagehide', flushOnHide);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flushOnHide();
    });
  }
  const entry = () => flush();
  hideFlushers.add(entry);
  return () => {
    hideFlushers.delete(entry);
  };
}

/** Largest seq of the given clips (0 when there are none). */
export function maxSeq(clips: ReadonlyArray<{ seq: number }>): number {
  return clips.reduce((max, c) => Math.max(max, c.seq), 0);
}

/** Truncates at a word boundary and adds an ellipsis. */
export function truncate(text: string, max: number): string {
  const clean = text.replace(/\s+/g, ' ').trim();
  if (clean.length <= max) return clean;
  const cut = clean.slice(0, max);
  const space = cut.lastIndexOf(' ');
  return `${(space > max * 0.6 ? cut.slice(0, space) : cut).trimEnd()} …`;
}

// ---------- exports and saved recordings ----------

export interface Placeholders {
  /** Some clip has no text yet (queued, being transcribed): exported as PENDING_LABEL. */
  pending: boolean;
  /** Some clip has no text because its transcription failed: exported as ERROR_LABEL. */
  failed: boolean;
  /** The labels the export prints, quoted, for a confirmation text. */
  labels: string;
}

/**
 * The placeholders an export prints instead of text (see export/common.clipBody), null when every
 * clip has text to print. A typed entry is done; a clip being transcribed again keeps (and exports)
 * its previous text.
 */
export function exportPlaceholders(clips: ReadonlyArray<Pick<Clip, 'status' | 'text'>>): Placeholders | null {
  const empty = clips.filter((c) => c.status !== 'done' && c.text.trim() === '');
  if (empty.length === 0) return null;
  const failed = empty.some((c) => c.status === 'error');
  const pending = empty.some((c) => c.status !== 'error');
  const labels = [pending && `»${PENDING_LABEL}«`, failed && `»${ERROR_LABEL}«`].filter(Boolean).join(' oziroma ');
  return { pending, failed, labels };
}

/** What is left of a saved recording when the page closes before its upload (see warnNotPersisted). */
export type UnsavedAudio = 'recoverable' | 'lost' | 'file';

const MSG_NOT_PERSISTED: Record<UnsavedAudio, string> = {
  recoverable:
    'Posnetek je shranjen le začasno – dokler se ne naloži na strežnik, ostane na voljo za obnovitev. Brez povezave ne zapirajte brskalnika.',
  lost: 'Posnetek je shranjen le začasno. Če zaprete brskalnik, preden se naloži na strežnik, se izgubi. Brez povezave ne zapirajte brskalnika.',
  file: 'Posnetek je shranjen le začasno. Če zaprete brskalnik, preden se naloži na strežnik, ga boste morali naložiti znova.',
};

/**
 * Warns that a saved recording is only kept in memory (addAudioClip resolved `persisted: false`).
 * `audio`: a crash-recovery copy is kept until the upload succeeds, there is none, or the audio is
 * a file the user picked.
 */
export function warnNotPersisted(audio: UnsavedAudio): void {
  toast(MSG_NOT_PERSISTED[audio], { kind: 'warning', durationMs: 0 });
}
